import { useMemo, useState } from "react"
import { useRenewalTimelineQuery } from "@/v2-backoffice-client/license/renewals/license.renewals.query"
import LicenseRenewalChart, {
  type RenewalChartMode,
} from "@/v2-backoffice-client/license/renewals/LicenseRenewalChart"
import LicenseRenewalStats from "@/v2-backoffice-client/license/renewals/LicenseRenewalStats"
import LicenseRenewalTable from "@/v2-backoffice-client/license/renewals/LicenseRenewalTable"
import {
  buildRenewalEvents,
  buildRenewalBuckets,
  buildRenewalStats,
  RENEWAL_HORIZON_MONTHS,
  type RenewalGranularity,
} from "@/v2-backoffice-client/license/renewals/license.renewals.utils"

// The Renewals board: the book's committed cashflow projected forward from
// the Fenerum mirror — every renewal that will land, every scheduled
// subscription arriving, and every renewal that will NOT happen (churn).
// Owns the ONE query; the chart, stat cards and events table are
// presentational. The chart's view mode, bucketing and horizon live here so
// a bar click can focus the table.
function LicenseRenewals() {
  const { data, previousData, loading, error } = useRenewalTimelineQuery()
  // previousData keeps the board rendered through refetches.
  const items = (data ?? previousData)?.getRenewalTimeline ?? []

  const [mode, setMode] = useState<RenewalChartMode>("cash")
  const [granularity, setGranularity] = useState<RenewalGranularity>("month")
  const [horizonMonths, setHorizonMonths] = useState(RENEWAL_HORIZON_MONTHS)
  const [selectedBucketKey, setSelectedBucketKey] = useState<string | null>(
    null,
  )

  const { events, buckets, stats } = useMemo(() => {
    const events = buildRenewalEvents(items, horizonMonths)
    const buckets = buildRenewalBuckets(items, events, {
      horizonMonths,
      granularity,
    })
    return {
      events,
      buckets,
      stats: buildRenewalStats(items, events, buckets, horizonMonths),
    }
  }, [items, horizonMonths, granularity])

  const selectedBucket =
    buckets.find((bucket) => bucket.key === selectedBucketKey) ?? null

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto">
      <LicenseRenewalStats stats={stats} />

      <LicenseRenewalChart
        buckets={buckets}
        currency={stats.currency}
        mode={mode}
        onModeChange={setMode}
        granularity={granularity}
        onGranularityChange={(next) => {
          setGranularity(next)
          setSelectedBucketKey(null)
        }}
        horizonMonths={horizonMonths}
        onHorizonChange={(next) => {
          setHorizonMonths(next)
          setSelectedBucketKey(null)
        }}
        selectedBucketKey={selectedBucketKey}
        onBucketClick={(bucketKey) => {
          setSelectedBucketKey((current) =>
            current === bucketKey ? null : bucketKey,
          )
        }}
      />

      <LicenseRenewalTable
        events={events}
        isLoading={loading && items.length === 0}
        error={error ?? undefined}
        selectedMonthKeys={selectedBucket?.monthKeys ?? null}
      />
    </div>
  )
}

export default LicenseRenewals
